<template>
  <ReportModal
    :is-open="showModal"
    :modal-title="modalTitle"
    @modalIsClose="closeModal()"
  >
    <template #content>
      <Preview :item="currentItem" />
    </template>
    <template #actions>
      <div class="flex justify-between w-full">
        <Create
          :label="'Geral'"
          :icon="'fa-solid fa-file-invoice'"
          @click="generateGeneralReport(currentItem.id)"
        />
        <Create
          :label="'Item atual'"
          :icon="'fa-solid fa-dolly'"
          @click="generateItemReport(currentItem.id)"
        />
      </div>
    </template>
  </ReportModal>

  <table
    class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
  >
    <thead class="text-xs text-gray-700 uppercase bg-gray-50">
      <tr>
        <slot name="head" />
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="(row, index) in data"
        :key="index"
        class="bg-white border-b hover:bg-gray-50"
      >
        <!-- foto, nome e descrição -->
        <th
          scope="row"
          class="flex items-center px-6 py-4 text-gray-900 whitespace-nowrap"
        >
          <!-- foto -->
          <img
            class="w-10 h-10 rounded-full"
            :src="!row['image'] ? defaultImg : imageServe + row['image']"
            :alt="'Foto de ' + row['name']"
          />

          <!-- nome e estoque -->
          <div class="ps-3">
            <div class="text-base font-semibold">
              {{
                row["name"].length > 20
                  ? row["name"].slice(0, 20).concat("...")
                  : row["name"]
              }}
            </div>

            <div
              class="font-normal text-green-500"
              :class="[
                row['inventory'] == 0 || row['inventory'] < 0
                  ? 'text-red-500'
                  : '',
                row['inventory'] > 1 && row['inventory'] < 30
                  ? 'text-yellow-500'
                  : '',
              ]"
            >
              {{ row["inventory"] ? "Estoque: " + row["inventory"] : "" }}
            </div>
          </div>
        </th>

        <!-- descrição e tamanho -->
        <td scope="row" class="px-6 py-4 whitespace-nowrap">
          <div>
            <div title="Descrição" class="text-base text-gray-500">
              {{
                row["description"]?.length > 20
                  ? row["description"].slice(0, 20).concat("...")
                  : row["description"] ?? "não informado"
              }}
            </div>

            <div class="font-normal text-gray-500 lowercase">
              {{ row["size"] ? "Tamanho: " + row["size"] : "" }}
            </div>
          </div>
        </td>

        <!-- CA -->
        <td scope="row" class="px-6 py-4 whitespace-nowrap">
          <div>
            <div title="Qtd. estoque" class="text-base">
              {{ row["CA"] ? "CA: " + row["CA"] : "CA não informado" }}
            </div>

            <div class="font-normal text-gray-500 lowercase">
              {{
                row["validity"]
                  ? "Validade CA: " + formatDate(row["validity"])
                  : ""
              }}
            </div>
          </div>
        </td>

        <!-- ações -->
        <td class="px-6 py-4">
          <div class="flex justify-center font-medium">
            <!-- editar -->
            <router-link
              title="Editar item"
              class="text-yellow-600 mr-1 hover:underline cursor-pointer"
              :to="'/stock/edit/' + row['id']"
            >
              <component :is="PhNotePencil" class="h-6 w-6" />
            </router-link>

            <!-- relatórios -->
            <a
              title="Gerar relatório"
              @click="showReport(row)"
              class="text-green-600 ml-1 hover:underline cursor-pointer"
            >
              <component :is="PhFileDoc" class="h-6 w-6" />
            </a>

            <!-- remover -->
            <a
              :title="'Desativar ' + row['name'] + ' da lista'"
              @click="deleteItem(row['id'])"
              class="text-red-600 ml-1 hover:underline cursor-pointer"
            >
              <component :is="PhTrash" class="h-6 w-6" />
            </a>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import ReportModal from "@/components/Modals/Reports.vue";
import Create from "@/components/Buttons/Create.vue";
import Preview from "./../layout/Report/Preview.vue";
import moment from "moment";
import { PhFileDoc, PhNotePencil, PhTrash } from "@phosphor-icons/vue";
import { PdfCreate } from "@/utils/reports/generate-blob";
import { showToast as toast } from "@/helpers/UseToasts";
import { defineProps, ref } from "vue";
import { storeApp } from "@/store";
import stockService, { PATH } from "../services/stock.service";

const imageServe = process.env.VUE_APP_IMAGE_SERVE + "storage/";
const defaultImg = require("@/assets/logo/icon.png");
const showModal = ref(false);
const modalTitle = ref("Selecione o relatório que deseja");
const currentItem = ref({});

const props = defineProps({
  data: [Object, Array],
});

const emit = defineEmits(["delete"]);

function showReport(itemData) {
  currentItem.value = itemData;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  currentItem.value = {};
}

async function generateItemReport(id) {
  storeApp.state.loading = true;

  const url = `${process.env.VUE_APP_API_URL}${PATH}reports/${id}/item`;
  const report = new PdfCreate(url);

  return await report.fetchPdf().finally(() => {
    storeApp.state.loading = false;
  });
}

async function generateGeneralReport(id) {
  storeApp.state.loading = true;

  const url = `${process.env.VUE_APP_API_URL}${PATH}reports/${id}`;
  const report = new PdfCreate(url);

  return await report.fetchPdf().finally(() => {
    storeApp.state.loading = false;
  });
}

async function deleteItem(id) {
  storeApp.state.loading = true;
  const confirm = window.confirm("Deseja realmente remover esse item?");
  if (confirm) {
    stockService
      .deleteIten(id)
      .then((res) => {
        if (res.data.success) {
          toast("Sucesso", "Item removido", "success");
        }
        window.location.reload();
      })
      .catch((err) => {
        toast("Aviso", "Item não foi removido", "error");
      });
  }
  storeApp.state.loading = false;
}

function formatDate(date) {
  if (date !== null) {
    return moment(date).format("D/MM/Y");
  }

  return "não informado";
}
</script>
